import type { DashboardFilterValues } from '@/app/(authed)/(properties)/dashboard/page.tsx';
import { SearchableDropdown } from '@/app/components/SearchableDropdown';
import React from 'react';
import { useDebounce } from 'utils';
import { useGetAccountPropertyManagementProperties } from './useAccountPropertyManagementCompanies';
import { useGetUserPropertiesAssignedAgents } from './userPropertiesAssignedAgents';

interface DashboardFiltersProps {
  filters: DashboardFilterValues;
  setFilters: React.Dispatch<React.SetStateAction<DashboardFilterValues>>;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = props => {
  const [companySearch, setCompanySearch] = React.useState<string>('');

  const debouncedCompanySearch = useDebounce(companySearch, 500);

  const updateFilters = (newFilters: Partial<DashboardFilterValues>) => {
    props.setFilters({ ...props.filters, ...newFilters });
  };

  const {
    data: companyData,
    isLoading: companyLoading,
    isError: companyError,
  } = useGetAccountPropertyManagementProperties({
    search: debouncedCompanySearch,
  });

  const {
    data: assignedAgentsData,
    isLoading: assignedAgentsLoading,
    isError: assignedAgentsError,
  } = useGetUserPropertiesAssignedAgents();

  return (
    <div className="m-6 flex flex-row justify-between">
      <div className="flex flex-row items-start justify-start gap-3">
        <SearchableDropdown
          labelPrefix="Assigned to:"
          placeholder="Search agents..."
          options={
            assignedAgentsData?.assignedTo
              ?.filter((agent): agent is string => typeof agent === 'string' && !!agent)
              .map((agent: string) => ({
                label: agent,
                value: agent,
              })) ?? []
          }
          isOptionsLoading={assignedAgentsLoading}
          optionsError={assignedAgentsError ? 'Error fetching agents' : null}
          onChange={value => updateFilters({ assigned: value })}
          selectedValue={props.filters.assigned || null}
          allowClear={true}
        />
        <SearchableDropdown
          labelPrefix="Company:"
          placeholder="Search companies..."
          options={companyData?.data?.map(pmc => ({ label: pmc.name, value: pmc.id })) ?? []}
          onSearchChange={setCompanySearch}
          searchValueExternallyControlled={companySearch}
          isOptionsLoading={companyLoading}
          optionsError={companyError ? 'Error fetching companies' : null}
          onChange={value => updateFilters({ company: value })}
          selectedValue={props.filters.company || null}
          allowClear={true}
        />
      </div>
    </div>
  );
};
